import React, { useState, useEffect, useMemo } from "react";
import { Table, Form, OverlayTrigger, Tooltip, Button, Spinner,Modal } from "react-bootstrap";
import ExportToExcel from "../../../../components/ExportToExcel";
import Pagination from "../../../../components/Pagination";
import SearchBar from "../../../../components/SearchBar";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { GetServiceCaller ,PostServiceCaller} from "../../../../services/serviceCaller";
import { API_URL } from "../../../../constants";

/*
 * FORMULA REFERENCE (for editable field calculations only):
 * ==================
 * GROSS = A
 * Prev. Ann. Bonus = E
 * % Bonus (Mgmt) = Z2 (EDITABLE INPUT)
 * Bonus Amt (Mgmt) = Z2 * A / 100 = Z4
 * Bonus Amt. = Z4
 * Delta Bonus = Z4 - E = Z5
 * Bonus Amt Inc.% = Z5/E * 100
 */

// Helper function to parse numeric strings to numbers
const parseNumeric = (value) => {
    if (!value) return 0;
    if (typeof value === 'string') {
        return parseFloat(value) || 0;
    }
    return parseFloat(value) || 0;
};

// Helper function to parse percentage strings to numbers
const parsePercentage = (value) => {
    if (!value) return 0;
    if (typeof value === 'string') {
        return parseFloat(value.replace('%', '')) || 0;
    }
    return parseFloat(value) || 0;
};


// Calculate only the fields that depend on editable bonusPercentMgmt
const calculateMgmtBonusFields = (row) => {
    
    const A = parseNumeric(row.Gross) || parseNumeric(row.gross);           // GROSS
    const E = parseNumeric(row.PreAnnBonus) || parseNumeric(row.prevAnnBonus); // Prev. Ann. Bonus
    let Z2 = parseFloat(row.bonusPercentMgmt) || 0; // % Bonus (Mgmt) - editable

    if (row.bonusPercentMgmt == "") {
        Z2 = row.bonusPercentAsPerBand;
    }

    // Bonus Amt (Mgmt) = Z2 * A / 100 = Z4
    const bonusAmtMgmt = (Z2 * A) / 100;

    // Bonus Amt. = Z4
    const bonusAmt = bonusAmtMgmt;

    // Delta Bonus = Z4 - E = Z5
    const deltaBonus = bonusAmtMgmt - E;

    // Bonus Amt Inc.% = Z5/E * 100
    const bonusAmtIncPercent = E !== 0 ? (deltaBonus / E * 100) : 0;

    return {
        bonusAmtMgmt: Z2 ? parseFloat(bonusAmtMgmt.toFixed(2)) : 0,
        bonusAmt: Z2 ? parseFloat(bonusAmt.toFixed(2)) : 0,
        deltaBonus: Z2 ? parseFloat(deltaBonus.toFixed(2)) : 0,
        bonusAmtIncPercent: Z2 ? parseFloat(bonusAmtIncPercent.toFixed(2)) : 0,
    };
};

// Transform API data to match component structure (keep original calculated values)
const transformApiData = (apiData) => {
    return apiData.map((item, index) => {
        // Parse all values from API
        const row = {
            id: index + 1,
            empId: item.EmpCode,
            name: item.EmpName,
            CycleID:item.CycleID,
            designation: item.Designation,
            doj: item.DOJ,
            totalExp: item.TotalExp,
            niceExp: item.NiceExp,

            PrimarySkills: item.PrimarySkills,
            SecondarySkills: item.SecondarySkills,
            AwardScores: item.AwardScores,
            LNDScores: item.LNDScores,

            // Use API calculated values directly
            gross: parseNumeric(item.Gross),
            prevGross: parseNumeric(item.PrevGross),
            grossIncPercent: parsePercentage(item.GrossInc_Per),
            ctc: parseNumeric(item.CurrentCTC),
            annBonus: parseNumeric(item.CurrentBonus),
            prevAnnBonus: parseNumeric(item.PreAnnBonus),
            prevBonusPercent: parsePercentage(item.PrevBonusPer),
            aprPercent: parsePercentage(item.APR_Per),
            grading: item.Grading,
            bonusBand: item.BonusBand,
            bonusPercentAsPerBand: parsePercentage(item.Bonus),
            bonusAmtAsPerBand: parseNumeric(item.BonusAsPerBand),
        
            bonusPercentMgmt: item.BonusPercentMgmt,
            bonusAmtMgmt: parseNumeric(item.BonusAmtMgmt),
            bonusAmt: parseNumeric(item.BonusAmt),
            deltaBonus: parseNumeric(item.DeltaBonus),
            bonusAmtIncPercent: parsePercentage(item.BonusAmtIncPercent),
            Remarks:item.Remarks,
            
            totalRevenue: parseNumeric(item.AnnualRevenue),
            grossMargin: parsePercentage(item.GrossMargin),

            // Add highlight flags from API
            PayrollIncrementCheck: Number(item.PayrollIncrementCheck),
            joinSeptCheck: Number(item.joinSeptCheck),
            DubaiShiftCheck: Number(item.DubaiShiftCheck),
        };

        return row;
    });
};

const columns = {
    empId: { text: "EMP ID", width: "100px", visible: true, align: "center" },
    name: { text: "NAME", width: "150px", visible: true, align: "left" },
    designation: { text: "DESIGNATION", width: "200px", visible: true, align: "left" },
    doj: { text: "DOJ", width: "100px", visible: true, align: "center" },
    totalExp: { text: "Total. Exp", width: "90px", visible: true, align: "center" },
    niceExp: { text: "NICE Exp.", width: "90px", visible: true, align: "center" },

    PrimarySkills: { text: "PrimarySkills", width: "180px", visible: true, align: "center" },
    SecondarySkills: { text: "SecondarySkills", width: "180px", visible: true, align: "center" },
    AwardScores: { text: "AwardScores", width: "90px", visible: true, align: "center" },
    LNDScores: { text: "LNDScores", width: "90px", visible: true, align: "center" },

    gross: { text: "GROSS (A)", width: "100px", visible: true, align: "right" },
    prevGross: { text: "Prev. GROSS (B)", width: "110px", visible: true, align: "right" },
    grossIncPercent: { text: "Gross Inc.% ((A-B)/B)", width: "120px", visible: true, align: "right", calculated: false },
    ctc: { text: "CTC (C)", width: "100px", visible: true, align: "right" },
    annBonus: { text: "Ann. Bonus (D)", width: "110px", visible: true, align: "right" },
    prevAnnBonus: { text: "Prev. Ann. Bonus (E)", width: "130px", visible: true, align: "right" },
    prevBonusPercent: { text: "Prev. Bonus % (E/B)", width: "120px", visible: true, align: "right", calculated: false },
    aprPercent: { text: "APR % (X)", width: "80px", visible: true, align: "right" },
    grading: { text: "Grading (Y)", width: "90px", visible: true, align: "center" },
    bonusBand: { text: "Bonus Band (Z)", width: "120px", visible: true, align: "center" },
    bonusPercentAsPerBand: { text: "Bonus % (as per band) (Z1)", width: "150px", visible: true, align: "right" },
    bonusAmtAsPerBand: { text: "Bonus Amt. (as per band) (Z1×A)", width: "160px", visible: true, align: "right", calculated: false },
    bonusPercentMgmt: { text: "% Bonus (Mgmt) (Z2)", width: "130px", visible: true, align: "center", editable: true },
    bonusAmtMgmt: { text: "Bonus Amt (Mgmt) (Z2×A=Z4)", width: "160px", visible: true, align: "right", calculated: false },
    bonusAmt: { text: "Bonus Amt. (Z4)", width: "110px", visible: true, align: "right", calculated: false },
    deltaBonus: { text: "Delta Bonus (Z4-E=Z5)", width: "140px", visible: true, align: "right", calculated: false },
    bonusAmtIncPercent: { text: "Bonus Amt Inc.% (Z5/E)", width: "130px", visible: true, align: "right", calculated: false },
    totalRevenue: { text: "Total Revenue", width: "130px", visible: true, align: "right", calculated: false },
    grossMargin: { text: "Gross Margin(%)", width: "130px", visible: true, align: "right", calculated: false },
    Remarks: {text: "Remarks", editable: true, width: "250px", align: "left", visible: true}
};

const AnnualBonusReport = ({
    onDataChange,
    onSubmitSuccess,
    apiEndpoint = `${API_URL}store-bonus-working-data`,
    className = "",
}) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingCell, setEditingCell] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRecordsPerPage, setSelectedRecordsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: "empId", direction: "ascending" });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [visibleColumns, setVisibleColumns] = useState(
        Object.keys(columns).reduce((acc, key) => {
            acc[key] = columns[key].visible;
            return acc;
        }, {})
    );

    const ExcelName = "Annual_Bonus_Report";

    // Fetch data from API
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await GetServiceCaller(`${API_URL}get-bonus-working-data`);
                if (response.data.status === 'success' && response.data.data) {
                    const transformedData = transformApiData(response.data.data);             
                    setData(transformedData);
                    // toast.success('Data loaded successfully!');
                } else {
                    toast.error('Failed to load data: Invalid response format');
                }
            } catch (error) {
                console.error('API fetch error:', error);
                toast.error(`Error loading data: ${error.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Ensure sorting after data loads
    useEffect(() => {
        if (data.length > 0) {
            setSortConfig({ key: "empId", direction: "ascending" });
        }
    }, [data]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const toggleColumn = (columnName) => {
        setVisibleColumns((prevState) => ({
            ...prevState,
            [columnName]: !prevState[columnName],
        }));
    };

    const handleCellClick = (rowIndex, columnKey) => {
        if (columns[columnKey]?.editable) {
            setEditingCell({ rowIndex, columnKey });
        }
    };

    const handleCellChange = (rowIndex, columnKey, value) => {
        const updatedData = [...data];

        // Ensure numeric fields stay numbers, but allow 'remark' to be a string
        const processedValue = columnKey === "Remarks" 
            ? value 
            : (value === '' ? '' : Number(value));

        updatedData[rowIndex] = {
            ...updatedData[rowIndex],
            [columnKey]: processedValue,
        }
        updatedData[rowIndex] = {
            ...updatedData[rowIndex],
            [columnKey]: value,
        };

        // Recalculate only the fields that depend on bonusPercentMgmt
        if (columnKey === "bonusPercentMgmt") {
            const mgmtFields = calculateMgmtBonusFields(updatedData[rowIndex]);
            updatedData[rowIndex] = {
                ...updatedData[rowIndex],
                ...mgmtFields,
            };
        }
        
        setData(updatedData);
        if (onDataChange) {
            onDataChange(updatedData);
        }
    };

    const handleCellBlur = () => {
        setEditingCell(null);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            setEditingCell(null);
        }
    };

    const isEditing = (rowIndex, columnKey) => {
        return (
            editingCell &&
            editingCell.rowIndex === rowIndex &&
            editingCell.columnKey === columnKey
        );
    };

    const handleSort = (columnName) => {
        if (columns[columnName]?.editable) return;
        let direction = "ascending";
        if (sortConfig.key === columnName && sortConfig.direction === "ascending") {
            direction = "descending";
        }
        setSortConfig({ key: columnName, direction });
    };
    
    const formatDateToSQL = (dateStr) => {
        if (!dateStr) return null;
        
        const date = new Date(dateStr);
        
        // Check if the date is valid
        if (isNaN(date.getTime())) return null;

        const year = date.getFullYear();
        // getMonth() is 0-indexed, so we add 1 and pad with a leading zero
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };
    
    const [showConfirm, setShowConfirm] = useState(false);

    // Handle form submission
    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            // Prepare data for API
           const submitData = data.map(row => ({
            "EmpID": row.empId,
            "CycleID": row.CycleID,
            "EmpName": row.name,
            "Designation": row.designation,
            "Doj": formatDateToSQL(row.doj),
            "TotalExp": row.totalExp,
            "NiceExp": row.niceExp,

            "PrimarySkills": row.PrimarySkills,
            "SecondarySkills": row.SecondarySkills,
            "AwardScores": row.AwardScores,
            "LNDScores": row.LNDScores,

            "Gross": String(row.gross),
            "PrevGross": String(row.prevGross),
            "GrossIncPercent": String(row.grossIncPercent),
            "Ctc": String(row.ctc),
            "AnnBonus": row.annBonus,
            "PrevAnnBonus": row.prevAnnBonus,
            "PrevBonusPercent": row.prevBonusPercent,
            "AprPercent": row.aprPercent,
            "Grading": row.grading,
            "BonusBand": row.bonusBand,
            "BonusPercentBand": row.bonusPercentAsPerBand,
            "BonusAmtBand": row.bonusAmtAsPerBand,
            "BonusPercentMgmt": row.bonusPercentMgmt,
            "BonusAmtMgmt": row.bonusAmtMgmt,
            "BonusAmt": row.bonusAmt,
            "DeltaBonus": row.deltaBonus,
            "BonusAmtIncPercent": row.bonusAmtIncPercent,
            "AnnualRevenue":row.totalRevenue,
            "GrossMargin":row.grossMargin,
            "Remarks": row.Remarks || ""
        }));

            const response = await PostServiceCaller(
                apiEndpoint,
                submitData,
                "post"
            );
            console.log("post",submitData);

            if (response.status === 200 || response.status === 201) {
                toast.success("Annual Bonus Report submitted successfully!");
                if (onSubmitSuccess) {
                    onSubmitSuccess(response.data);
                }
            }
        } catch (error) {
            console.error("Submit error:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to submit data";
            toast.error(`Error: ${errorMessage}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const sortedData = useMemo(() => {
        if (!Array.isArray(data) || !sortConfig.key) return data || [];

        return [...data].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            if (aVal === null || aVal === undefined || aVal === "") return 1;
            if (bVal === null || bVal === undefined || bVal === "") return -1;

            let comparison = 0;
            if (typeof aVal === "number" && typeof bVal === "number") {
                comparison = aVal - bVal;
            } else {
                comparison = String(aVal).localeCompare(String(bVal), undefined, { 
                    numeric: true, 
                    sensitivity: 'base' 
                });
            }

            return sortConfig.direction === "ascending" ? comparison : -comparison;
        });
    }, [data, sortConfig]);

    const filteredData = sortedData.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    const totals = useMemo(() => {
        return filteredData.reduce((acc, row) => {
            acc.annBonus += parseNumeric(row.annBonus) || 0;
            acc.bonusAmtAsPerBand += parseNumeric(row.bonusAmtAsPerBand) || 0;
            acc.bonusAmt += parseNumeric(row.bonusAmt) || 0;
            acc.deltaBonus += parseNumeric(row.deltaBonus) || 0;
            return acc;
        }, { 
            bonusAmt: 0, 
            deltaBonus: 0, 
            annBonus: 0, 
            bonusAmtAsPerBand: 0 
        });
    }, [filteredData]);

    const currentData = filteredData.slice(
        (currentPage - 1) * selectedRecordsPerPage,
        currentPage * selectedRecordsPerPage
    );

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleRecordsPerPage = (e) => {
        const newRecordsPerPage = Number(e.target.value);
        const currentStartIndex = (currentPage - 1) * selectedRecordsPerPage;
        const newPage = Math.floor(currentStartIndex / newRecordsPerPage) + 1;
        setSelectedRecordsPerPage(newRecordsPerPage);
        setCurrentPage(newPage);
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === "" || isNaN(value)) return value;
        return parseFloat(value).toLocaleString("en-IN");
    };

    const renderCellValue = (row, columnKey) => {
        const value = row[columnKey];

        const currencyFields = [
            "gross", "prevGross", "ctc", "annBonus", "prevAnnBonus",
            "bonusAmtAsPerBand", "bonusAmtMgmt", "bonusAmt", "deltaBonus"
        ];

        if (currencyFields.includes(columnKey) && value !== null && value !== undefined && value !== "") {
            return formatCurrency(value);
        }

        const percentFields = [
            "grossIncPercent", "prevBonusPercent", "aprPercent",
            "bonusPercentAsPerBand", "bonusAmtIncPercent", "bonusAmtIncPercent","grossMargin"
        ];

        if (percentFields.includes(columnKey) && value !== null && value !== undefined && value !== "") {
            return `${value}%`;
        }

        return value;
    };

    const recordsPerPageOption = [
        { value: 10, label: "10" },
        { value: 25, label: "25" },
        { value: 50, label: "50" },
        { value: 100, label: "100" },
        { value: filteredData.length || 1, label: "All" },
    ];

    const getExportData = () => {
        return currentData.map((item) => {
            const visibleItem = {};
            Object.keys(columns)
                .filter((columnName) => visibleColumns[columnName])
                .forEach((columnName) => {
                    visibleItem[columns[columnName].text] = item[columnName];
                });
            return visibleItem;
        });
    };

    return (
        <div className={`container-fluid ${className}`}>
            <style>
                {`
                    .custom-scrollbar::-webkit-scrollbar {
                        width: 12px;
                        height: 12px;
                        display: block !important;
                    }
                    .custom-scrollbar::-webkit-scrollbar-track {
                        background: #f8f9fa;
                        border-radius: 10px;
                        border: 1px solid #dee2e6;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                        background: #adb5bd;
                        border-radius: 10px;
                        border: 3px solid #f8f9fa;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: #27568b;
                    }
                    /* For Firefox */
                    .custom-scrollbar {
                        scrollbar-width: auto;
                        scrollbar-color: #adb5bd #f8f9fa;
                    }
                    /* Sticky column styles */
                    .sticky-col {
                        position: sticky;
                        background-color: #fff;
                        z-index: 5;
                    }
                    .sticky-col-header {
                        position: sticky;
                        top: 0;
                        z-index: 15;
                        background-color: #27568b !important;
                        color: #ffffff;
                    }
                `}
            </style>
            <div className="card shadow-sm border-0">
                <div className="card-header text-white" style={{ backgroundColor: "#27568b" }}>
                    <h5 className="mb-0 d-flex align-items-center">
                        <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                        Bonus Working Report
                    </h5>
                </div>
                <div className="card-body">
                    {/* Toolbar */}
                    <div className="d-flex flex-wrap justify-content-between mb-3">
                        <div className="d-flex mb-2 mb-md-0">
                            <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                        </div>
                        <div className="d-flex align-items-center">
                            <ExportToExcel data={getExportData()} filename={`${ExcelName}.csv`} />
                            
                            {/* Column Chooser */}
                            <div className="btn-group ms-2" style={{ zIndex: 100 }}>
                                <button className="dropdown-toggle skill-button" data-bs-toggle="dropdown" id="excel-btn">
                                    <i className="bi bi-list"></i>
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end p-2" style={{ maxHeight: "300px", overflowY: "auto" }}>
                                    {Object.keys(columns).map((columnName) => (
                                        <Form.Check
                                            key={columnName}
                                            type="checkbox"
                                            id={`check-${columnName}`}
                                            label={columns[columnName].text}
                                            checked={visibleColumns[columnName] || false}
                                            onChange={() => toggleColumn(columnName)}
                                        />
                                    ))}
                                </ul>
                            </div>

                            {/* Submit Button */}
                            <Button
                                className="ms-2"
                                onClick={() => setShowConfirm(true)}
                                // disabled={isSubmitting}
                                disabled
                                style={{ height: "35px", backgroundColor: "#27568b", border: "none" }}
                            >
                                {isSubmitting ? (
                                    <Spinner size="sm" animation="border" />
                                ) : (
                                    <><i className="bi bi-send me-1"></i> Submit</>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Color Legend - Added from AnnualAppraisalReport */}
                    <div className="mb-3 p-2 rounded d-flex flex-wrap align-items-center gap-4" 
                        style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", fontSize: "11px" }}>
                        <div className="d-flex align-items-center gap-2">
                            <div style={{ width: "16px", height: "16px", backgroundColor: "#D4EDDA", border: "1px solid #e0d1a0", borderRadius: "3px" }}></div>
                            <span className="fw-bold">Payroll Increment After April</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <div style={{ width: "16px", height: "16px", backgroundColor: "#CCE5FF", border: "1px solid #b38f2d", borderRadius: "3px" }}></div>
                            <span className="fw-bold">Dubai Shift</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <div style={{ width: "16px", height: "16px", backgroundColor: "#FFE5D0", border: "1px solid #00bb16", borderRadius: "3px" }}></div>
                            <span className="fw-bold">Joiners After Oct.</span>
                        </div>
                        <div style={{ width: "1px", height: "20px", backgroundColor: "#dee2e6" }}></div>
                        {loading ? (
                            <div className="d-flex align-items-center gap-2">
                                <Spinner size="sm" variant="primary" />
                                <span className="text-muted">Loading data...</span>
                            </div>
                        ) : data.length > 0 ? (
                            <>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="fw-bold">Total Records:</span>
                                    <span className="bg-white px-2 py-1 rounded border">
                                        {filteredData.length} employees
                                    </span>
                                </div>

                                {/* Added Total Bonus Amt */}
                                <div className="d-flex align-items-center gap-2">
                                    <span className="fw-bold text-primary">Total D:</span>
                                    <span className="bg-white px-2 py-1 rounded border fw-bold">
                                        {formatCurrency(Math.round(totals.annBonus))}
                                    </span>
                                </div>

                                {/* Added Total Bonus Amt */}
                                <div className="d-flex align-items-center gap-2">
                                    <span className="fw-bold text-primary">Total Z1xA:</span>
                                    <span className="bg-white px-2 py-1 rounded border fw-bold">
                                        {formatCurrency(Math.round(totals.bonusAmtAsPerBand))}
                                    </span>
                                </div>


                                {/* Added Total Bonus Amt */}
                                <div className="d-flex align-items-center gap-2">
                                    <span className="fw-bold text-danger">Total Z4:</span>
                                    <span className="bg-white px-2 py-1 rounded border fw-bold">
                                        {formatCurrency(Math.round(totals.bonusAmt))}
                                    </span>
                                </div>

                                {/* Added Total Delta Bonus */}
                                <div className="d-flex align-items-center gap-2">
                                    <span className="fw-bold text-primary">Total Delta:</span>
                                    <span className="bg-white px-2 py-1 rounded border fw-bold">
                                        {formatCurrency(Math.round(totals.deltaBonus))}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <span className="text-muted">No data available</span>
                        )}
                    </div>


                    {/* Table Container - With Sticky Columns */}
                    <div className="border rounded position-relative custom-scrollbar" style={{ 
                        overflow: "auto",
                        maxHeight: "65vh",
                        minHeight: "350px",
                        backgroundColor: "#fff",
                        display: "block"
                    }}>
                        
                        {loading && (
                            <div 
                                className="position-absolute w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white" 
                                style={{ zIndex: 20, opacity: 0.9 }}
                            >
                                <Spinner animation="border" style={{ color: "#27568b" }} />
                                <span className="mt-2 fw-bold" style={{ color: "#27568b" }}>Fetching Annual Bonus ...</span>
                            </div>
                        )}

                        <Table striped bordered hover className="mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                            <thead className="text-center">
                                <tr>
                                    {/* Sticky Emp ID Header */}
                                    {visibleColumns.empId && (
                                        <th
                                            onClick={() => !columns.empId.editable && handleSort("empId")}
                                            style={{
                                                position: "sticky",
                                                left: 0,
                                                top: 0,
                                                zIndex: 19,
                                                backgroundColor: "#27568b",
                                                color: "#ffffff",
                                                whiteSpace: "nowrap", 
                                                verticalAlign: "middle",
                                                lineHeight: "1.2",
                                                fontSize: "13px",
                                                minWidth: columns.empId.width || "100px",
                                                cursor: columns.empId.editable ? "default" : "pointer",
                                                borderBottom: "1px solid #dee2e6",
                                                borderRight: "1px solid #dee2e6"
                                            }}
                                        >
                                            <div className="d-flex align-items-center justify-content-center">
                                                {columns.empId.text}
                                                {!columns.empId.editable && (
                                                    <span className="ms-1" style={{ fontSize: "10px", opacity: 0.8 }}>
                                                        {sortConfig.key === "empId" ? (
                                                            sortConfig.direction === "ascending" ? "▲" : "▼"
                                                        ) : (
                                                            "▽"
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    )}

                                    {/* Sticky Name Header */}
                                    {visibleColumns.name && (
                                        <th
                                            onClick={() => !columns.name.editable && handleSort("name")}
                                            style={{
                                                position: "sticky",
                                                left: visibleColumns.empId ? "100px" : "0",
                                                top: 0,
                                                zIndex: 18,
                                                backgroundColor: "#27568b",
                                                color: "#ffffff",
                                                whiteSpace: "nowrap", 
                                                verticalAlign: "middle",
                                                lineHeight: "1.2",
                                                fontSize: "13px",
                                                minWidth: columns.name.width || "150px",
                                                cursor: columns.name.editable ? "default" : "pointer",
                                                borderBottom: "1px solid #dee2e6",
                                                borderRight: "1px solid #dee2e6"
                                            }}
                                        >
                                            <div className="d-flex align-items-center justify-content-center">
                                                {columns.name.text}
                                                {!columns.name.editable && (
                                                    <span className="ms-1" style={{ fontSize: "10px", opacity: 0.8 }}>
                                                        {sortConfig.key === "name" ? (
                                                            sortConfig.direction === "ascending" ? "▲" : "▼"
                                                        ) : (
                                                            "▽"
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    )}

                                    {/* Rest of the columns (non-sticky) */}
                                    {Object.keys(columns).map((columnName) => {
                                        if (columnName === "empId" || columnName === "name") return null;
                                        return visibleColumns[columnName] && (
                                            <th
                                                key={columnName}
                                                onClick={() => !columns[columnName].editable && handleSort(columnName)}
                                                style={{
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 10,
                                                    backgroundColor: "#27568b",
                                                    color: "#ffffff",
                                                    whiteSpace: "nowrap", 
                                                    verticalAlign: "middle",
                                                    lineHeight: "1.2",
                                                    fontSize: "13px",
                                                    minWidth: columns[columnName].width || "110px",
                                                    cursor: columns[columnName].editable ? "default" : "pointer",
                                                    borderBottom: "1px solid #dee2e6"
                                                }}
                                            >
                                                <div className="d-flex align-items-center justify-content-center">
                                                    {columns[columnName].text}
                                                    {columns[columnName].editable && (
                                                        <i className="bi bi-pencil-fill ms-1 text-info" style={{ fontSize: "10px" }}></i>
                                                    )}
                                                    {!columns[columnName].editable && (
                                                        <span className="ms-1" style={{ fontSize: "10px", opacity: 0.8 }}>
                                                            {sortConfig.key === columnName ? (
                                                                sortConfig.direction === "ascending" ? "▲" : "▼"
                                                            ) : (
                                                                "▽"
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody style={{ fontSize: "13px" }}>
                                {currentData.length === 0 && !loading ? (
                                    <tr>
                                        <td colSpan={Object.keys(columns).length + 1} className="text-center py-4 text-muted">
                                            No records found.
                                        </td>
                                    </tr>
                                ) : (
                                    currentData.map((row, rowIndex) => {
                                        const actualIndex = data.findIndex(item => item.id === row.id);
                                        
                                        // Check highlight conditions
                                        const isPayrollCheckHighlighted = String(row.PayrollIncrementCheck) === "1";
                                        const isjoinafterSeptHighlighted = String(row.joinSeptCheck) === "1";
                                        const isDubaiShiftCheckHighlighted = String(row.DubaiShiftCheck) === "1";
                                        
                                        return (
                                            <tr key={row.id || rowIndex}>
                                                {/* Sticky Emp ID */}
                                                {visibleColumns.empId && (
                                                    <td
                                                        onClick={() => handleCellClick(actualIndex, "empId")}
                                                        style={{
                                                            position: "sticky",
                                                            left: 0,
                                                            backgroundColor: isPayrollCheckHighlighted ? '#D4EDDA' : 
                                                                           (isDubaiShiftCheckHighlighted ? '#CCE5FF' : 
                                                                           (isjoinafterSeptHighlighted ? '#FFE5D0' : '#fff')),
                                                            zIndex: 5,
                                                            textAlign: columns.empId.align || "center",
                                                            cursor: columns.empId.editable ? "pointer" : "default",
                                                            borderRight: "1px solid #dee2e6"
                                                        }}
                                                    >
                                                        {row.empId}
                                                    </td>
                                                )}

                                                {/* Sticky Name */}
                                                {visibleColumns.name && (
                                                    <td
                                                        onClick={() => handleCellClick(actualIndex, "name")}
                                                        style={{
                                                            position: "sticky",
                                                            left: visibleColumns.empId ? "100px" : "0",
                                                            backgroundColor: isPayrollCheckHighlighted ? '#D4EDDA' : 
                                                                           (isDubaiShiftCheckHighlighted ? '#CCE5FF' : 
                                                                           (isjoinafterSeptHighlighted ? '#FFE5D0' : '#fff')),
                                                            zIndex: 5,
                                                            textAlign: columns.name.align || "left",
                                                            cursor: columns.name.editable ? "pointer" : "default",
                                                            borderRight: "1px solid #dee2e6"
                                                        }}
                                                    >
                                                        {row.name}
                                                    </td>
                                                )}

                                                {/* Rest of the columns */}
                                                {Object.keys(columns).map((columnName) => {
                                                    if (columnName === "empId" || columnName === "name") return null;
                                                    return visibleColumns[columnName] && (
                                                        <td
                                                            key={columnName}
                                                            onClick={() => handleCellClick(actualIndex, columnName)}
                                                            style={{
                                                                textAlign: columns[columnName].align || "left",
                                                                cursor: columns[columnName].editable ? "pointer" : "default",
                                                                backgroundColor: columns[columnName].editable ? "#fffde7" : 
                                                                               (isPayrollCheckHighlighted ? '#D4EDDA' : 
                                                                               (isDubaiShiftCheckHighlighted ? '#CCE5FF' : 
                                                                               (isjoinafterSeptHighlighted ? '#FFE5D0' : 'transparent'))),
                                                                maxWidth: columns[columnName].width || "auto",
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis"
                                                            }}
                                                        >
                                                            {columns[columnName].editable && isEditing(actualIndex, columnName) ? (
                                                                <Form.Control
                                                                    type={columnName === "Remarks" ? "text" : "number"}
                                                                    value={row[columnName] ?? ""}
                                                                    onChange={(e) => handleCellChange(actualIndex, columnName, e.target.value)}
                                                                    onBlur={handleCellBlur}
                                                                    autoFocus
                                                                    size="sm"
                                                                    style={{ 
                                                                        textAlign: columnName === "Remarks" ? "left" : "center", 
                                                                        border: "1px solid #27568b" 
                                                                    }}
                                                                />
                                                            ) : (
                                                                columns[columnName].editable ? (
                                                                    <div style={{
                                                                        border: "1px solid #dee2e6",
                                                                        borderRadius: "4px",
                                                                        backgroundColor: "#fff",
                                                                        minHeight: "28px",
                                                                        display: "flex",
                                                                        padding: "0 8px",
                                                                        alignItems: "center",
                                                                        justifyContent: columnName === "Remarks" ? "flex-start" : "center",
                                                                        color: "#333"
                                                                    }}>
                                                                        {row[columnName] ?? ""}
                                                                    </div>
                                                                ) : (
                                                                    // Add tooltips for PrimarySkills and SecondarySkills
                                                                    (columnName === "PrimarySkills" || columnName === "SecondarySkills") ? (
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={
                                                                                <Tooltip id={`tooltip-${columnName}-${row.id}`}>
                                                                                    {renderCellValue(row, columnName) || ""}
                                                                                </Tooltip>
                                                                            }
                                                                        >
                                                                            <span style={{
                                                                                display: "block",
                                                                                whiteSpace: "nowrap",
                                                                                overflow: "hidden",
                                                                                textOverflow: "ellipsis",
                                                                                maxWidth: columns[columnName].width || "180px",
                                                                                cursor: "help"
                                                                            }}>
                                                                                {renderCellValue(row, columnName) || ""}
                                                                            </span>
                                                                        </OverlayTrigger>
                                                                    ) : (
                                                                        renderCellValue(row, columnName) || ""
                                                                    )
                                                                )
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Footer */}
                    <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between mt-3 px-2">
                        <div className="d-flex align-items-center mb-2 mb-sm-0">
                            <span className="me-2 text-muted" style={{ fontSize: "13px" }}>
                                Showing {filteredData.length === 0 ? 0 : (currentPage - 1) * selectedRecordsPerPage + 1} to{" "}
                                {Math.min(currentPage * selectedRecordsPerPage, filteredData.length)} of {filteredData.length}
                            </span>
                            <select
                                value={selectedRecordsPerPage}
                                onChange={handleRecordsPerPage}
                                className="form-select form-select-sm"
                                style={{ width: "85px" }}
                            >
                                {recordsPerPageOption.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <Pagination
                            itemsPerPage={selectedRecordsPerPage}
                            totalItems={filteredData.length}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
            </div>

            {/* Submit Confirmation Modal */}
            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered size="sm">
                <Modal.Body className="p-4 text-center">
                    <h5 className="fw-bold mb-2">Ready to submit?</h5>
                    <p className="text-secondary small mb-4">
                        Please review your entries. Once submitted, these scores will be finalized for this cycle.
                    </p>
                    <div className="d-grid gap-2">
                        <Button 
                            style={{ backgroundColor: "#27568b", border: "none" }} 
                            onClick={() => { setShowConfirm(false); handleSubmit(); }}
                        >
                            Yes, Submit Now
                        </Button>
                        <Button 
                            variant="light" 
                            className="text-secondary"
                            onClick={() => setShowConfirm(false)}
                        >
                            No, Go Back
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AnnualBonusReport;